import { useEffect, useMemo } from 'react';
import {
    applyEdgeChanges,
    applyNodeChanges,
    Handle,
    Position,
    useEdges,
    useKeyPress,
    useNodes,
    useReactFlow,
    useStore,
} from 'reactflow';
import { useUpdateNodeInternals } from '@reactflow/core';
import HtmlTooltip from '@shared/components/ui/HtmlTooltip';
import { Box, useTheme } from '@mui/material';
import { tokens } from '@app/theme/themes';
import { useTranslation } from 'react-i18next';
import { HANDLE_MOVE_STEP, NODE_SIZES } from '@pages/schemes/constants';
import { nodesSelectionActiveSelector } from '@pages/schemes/utils';

const HANDLE_UPDATE_SPEED = 100; // less === faster

const CustomMovableHandle = ({
    style,
    nodeType,
    nodeId,
    nodeSelected,
    selectedHandleId,
    setSelectedHandleId,
    data,
    position,
    setInitialHandlePosition,
    handleId,
    label,
    movable,
    ...restProps
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const updateNodeInternals = useUpdateNodeInternals();
    const nodes = useNodes();
    const edges = useEdges();
    const { getNode } = useReactFlow();
    const isNodesSelectionActive = useStore(nodesSelectionActiveSelector);
    const arrowRightPressed = useKeyPress('ArrowRight');
    const arrowLeftPressed = useKeyPress('ArrowLeft');
    const arrowUpPressed = useKeyPress('ArrowUp');
    const arrowDownPressed = useKeyPress('ArrowDown');
    const ctrlArrowRightPressed = useKeyPress('Control+ArrowRight');
    const ctrlArrowLeftPressed = useKeyPress('Control+ArrowLeft');
    const isHandleSelected = selectedHandleId === handleId;
    const nodeWidth = NODE_SIZES[nodeType].width;
    const nodeHeight = NODE_SIZES[nodeType].height;
    const node = getNode(nodeId);
    const port = node.data.ports.find((port) => port.name === handleId);
    const handlePositionObject = initializeHandlePositionObject(data, position, handleId, nodeWidth, nodeHeight);
    const { t } = useTranslation();

    const isPortConnectedToZoneWithNetwork = useMemo(() => {
        if (!port?.name) {
            return false;
        }

        return edges.some((edge) => {
            const connectedZoneNode = getNode(edge.target);

            if (!connectedZoneNode) {
                return false;
            }

            const isEdgeForThisPort = edge.sourceHandle === port.name && edge.source === nodeId;

            return isEdgeForThisPort && Boolean(connectedZoneNode.data.network);
        });
    }, [edges, getNode, nodeId, port?.name]);

    const ipMustBeAssigned = isPortConnectedToZoneWithNetwork && !port['ip_address'];

    // // when data object deletes selectedHandle id, local state should be updated
    useEffect(() => {
        if (data.selectedHandleId === null) {
            setSelectedHandleId(null);
        }
    }, [setSelectedHandleId, data.selectedHandleId]);

    // active port selection via keyboard
    useEffect(() => {
        if (!isHandleSelected || isNodesSelectionActive) {
            return;
        }

        let portDiff;

        if (ctrlArrowRightPressed) {
            portDiff = 1;
        } else if (ctrlArrowLeftPressed) {
            portDiff = -1;
        }

        if (portDiff) {
            setSelectedHandleId((prevSelectedHandleId) => {
                const portNumberRegexMatch = prevSelectedHandleId.match(/\d+/)[0];
                const portNumber = portNumberRegexMatch ? parseInt(portNumberRegexMatch, 10) : null;
                const newPortNumber = portNumber + portDiff;

                let newHandleId;

                // if move left from port0, change to port9
                if (newPortNumber < 0) {
                    newHandleId = prevSelectedHandleId.replace(portNumber, data.ports.length - 1);
                } else if (newPortNumber > data.ports.length - 1) {
                    // if move right from port9, change to port0
                    newHandleId = prevSelectedHandleId.replace(portNumber, 0);
                } else {
                    newHandleId = prevSelectedHandleId.replace(portNumber, newPortNumber);
                }

                return newHandleId;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctrlArrowRightPressed, ctrlArrowLeftPressed]);

    // handle keyboard arrow interactions
    useEffect(() => {
        // disable handle movement if selected other handle or other node
        if (!isHandleSelected || !nodeSelected || isNodesSelectionActive || !movable) {
            return;
        }

        let repeatFn;
        if (arrowRightPressed) {
            if (belongsToTopBorder(handlePositionObject) || belongsToBottomBorder(handlePositionObject, nodeHeight)) {
                repeatFn = movePortRight;
            }
        } else if (arrowLeftPressed) {
            if (belongsToTopBorder(handlePositionObject) || belongsToBottomBorder(handlePositionObject, nodeHeight)) {
                repeatFn = movePortLeft;
            }
        } else if (arrowUpPressed) {
            if (belongsToLeftBorder(handlePositionObject) || belongsToRightBorder(handlePositionObject, nodeWidth)) {
                repeatFn = movePortUp;
            }
        } else if (arrowDownPressed) {
            if (belongsToLeftBorder(handlePositionObject) || belongsToRightBorder(handlePositionObject, nodeWidth)) {
                repeatFn = movePortDown;
            }
        }

        if (repeatFn) {
            onMouseDown(null, repeatFn, 'keyup');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arrowRightPressed, arrowLeftPressed, arrowUpPressed, arrowDownPressed]);

    const applyNewHandlePosition = () => {
        let newHandlePosition = position;

        if (belongsToTopBorder(handlePositionObject)) {
            newHandlePosition = Position.Top;
        } else if (belongsToBottomBorder(handlePositionObject, nodeHeight)) {
            newHandlePosition = Position.Bottom;
        } else if (belongsToRightBorder(handlePositionObject, nodeWidth)) {
            newHandlePosition = Position.Right;
        } else if (belongsToLeftBorder(handlePositionObject, nodeWidth)) {
            newHandlePosition = Position.Left;
        }
        setInitialHandlePosition(newHandlePosition);
    };

    const onMouseDown = (e, repeatFunction, stopEventType = 'mouseup') => {
        let handleMovesCount = 0;

        function moveHandle() {
            repeatFunction();

            applyNewHandlePosition();
            applyNodeChanges([], nodes);
            applyEdgeChanges([], edges);
            setTimeout(() => {
                updateNodeInternals(nodeId);
            }, 100);
            handleMovesCount++;
        }

        if (repeatFunction) {
            const intervalId = setInterval(moveHandle, HANDLE_UPDATE_SPEED);

            document.addEventListener(stopEventType, () => {
                if (intervalId) {
                    if (!handleMovesCount) {
                        moveHandle();
                    }

                    clearInterval(intervalId);
                }
            });
        }
    };

    const movePortDown = () => {
        if (handlePositionObject.top >= nodeHeight) {
            return;
        }
        handlePositionObject.top += HANDLE_MOVE_STEP;
    };

    const movePortRight = () => {
        if (handlePositionObject.left > nodeWidth - 5) {
            return;
        }
        handlePositionObject.left += HANDLE_MOVE_STEP;
    };

    const movePortLeft = () => {
        if (handlePositionObject.left <= 0) {
            return;
        }
        handlePositionObject.left -= HANDLE_MOVE_STEP;
    };

    const movePortUp = () => {
        if (handlePositionObject.top <= 0) {
            return;
        }
        handlePositionObject.top -= HANDLE_MOVE_STEP;
    };

    const handleStyle = useMemo(
        () => ({
            width: style?.width ?? '8px',
            height: style?.height ?? '8px',
            backgroundColor: nodeSelected && isHandleSelected ? '#4cceac' : 'black',
            borderRadius: 3,
            top: `${handlePositionObject.top}px`,
            left: `${handlePositionObject.left}px`,
            bottom: 0,
            right: 0,
            transform: 'translate(-50%, -50%)',
        }),
        [
            style?.width,
            style?.height,
            handlePositionObject.top,
            handlePositionObject.left,
            isHandleSelected,
            nodeSelected,
        ],
    );

    function getTooltipTitle() {
        if (ipMustBeAssigned) {
            return t('ip_address_is_required');
        }

        if (movable) {
            return isHandleSelected
                ? t('double_click_on_port_to_disable_movement')
                : t('double_click_on_port_to_enable_movement');
        }

        return t('port_cant_be_moved_in_readonly_mode');
    }

    if (!port) {
        return null;
    }

    return (
        <HtmlTooltip
            followCursor={true}
            PopperProps={{
                sx: {
                    display: nodeSelected || ipMustBeAssigned ? 'block' : 'none',
                },
            }}
            placement="right-start"
            title={getTooltipTitle()}
        >
            <Handle
                {...restProps}
                type="source"
                position={position}
                id={handleId}
                style={handleStyle}
                onDoubleClick={
                    movable
                        ? (e) => {
                              if (!nodeSelected || e.target.parentElement.id.endsWith('-button')) {
                                  return;
                              }

                              setSelectedHandleId &&
                                  setSelectedHandleId((prevSelectedHandleId) => {
                                      if (prevSelectedHandleId === handleId) {
                                          data.selectedHandleId = null;
                                          return null;
                                      }

                                      data.selectedHandleId = handleId;
                                      return handleId;
                                  });

                              updateNodeInternals(nodeId);
                          }
                        : undefined
                }
            >
                {label && (
                    <Box sx={getPortLabelStyle(handlePositionObject, nodeWidth, nodeHeight, colors, ipMustBeAssigned)}>
                        {port['ip_address'] ? `${label} (${port['ip_address']})` : label}
                    </Box>
                )}
            </Handle>
        </HtmlTooltip>
    );
};

export default CustomMovableHandle;

function belongsToTopBorder(handlePositionObject) {
    return !handlePositionObject.top;
}

function belongsToBottomBorder(handlePositionObject, nodeHeight) {
    return handlePositionObject.top === nodeHeight;
}

function belongsToRightBorder(handlePositionObject, nodeWidth) {
    return handlePositionObject.left === nodeWidth;
}

function belongsToLeftBorder(handlePositionObject) {
    return handlePositionObject.left === 0;
}

function getPortLabelStyle(handlePositionObject, nodeWidth, nodeHeight, colors, isError) {
    let top;
    let left;

    if (belongsToBottomBorder(handlePositionObject, nodeHeight)) {
        top = 3;
        left = -21;
    } else if (belongsToRightBorder(handlePositionObject, nodeWidth)) {
        top = -10;
        left = 10;
    } else if (belongsToLeftBorder(handlePositionObject)) {
        top = -10;
        left = -24;
    } else if (belongsToTopBorder(handlePositionObject)) {
        top = -14;
        left = 7;
    }

    return {
        position: 'absolute',
        top: top,
        left: left,
        minWidth: '22px',
        display: 'inline-block',
        fontSize: '0.5rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: isError ? 'red' : colors.greenAccent[400],
    };
}

function calculateMiddleOfBiggestInterval(ports_positions, sideSize, sideProperty, position) {
    const positions = Object.values(ports_positions);
    const portsPositionArr = positions.filter((port) => port.reactflow_position === position);

    if (portsPositionArr.length === 0) {
        return null; // No ports on the given side
    }

    let startBorderObj = { [sideProperty]: 0 };
    let endBorderObj = { [sideProperty]: sideSize };
    const newPortsPositionArr = [startBorderObj, ...portsPositionArr, endBorderObj];

    // Sort the ports based on their position on the side
    newPortsPositionArr.sort((a, b) => a[sideProperty] - b[sideProperty]);

    // Find the biggest interval
    let maxInterval = 0;
    let middleOfBiggestInterval = 0;

    for (let i = 0; i < newPortsPositionArr.length - 1; i++) {
        const currentPort = newPortsPositionArr[i];
        const nextPort = newPortsPositionArr[i + 1];
        const intervalWidth = nextPort[sideProperty] - currentPort[sideProperty];

        if (intervalWidth > maxInterval) {
            maxInterval = intervalWidth;
            middleOfBiggestInterval = currentPort[sideProperty] + getRoundedMiddleOfInterval(intervalWidth);
        }
    }
    return middleOfBiggestInterval;
}

function getRoundedMiddleOfInterval(interval) {
    const halfNodeWidth = Math.floor(interval / 2);

    const remainder = halfNodeWidth % HANDLE_MOVE_STEP;

    if (remainder >= HANDLE_MOVE_STEP / 2) {
        return halfNodeWidth + HANDLE_MOVE_STEP - remainder;
    } else {
        return halfNodeWidth - remainder;
    }
}

export function initializeHandlePositionObject(data, position, handleId, nodeWidth, nodeHeight) {
    if (!data.ports_positions) {
        data.ports_positions = {};
    }

    if (!data.ports_positions[handleId]) {
        let topPosition;
        let leftPosition;

        if (position === Position.Left) {
            const middlePosition = calculateMiddleOfBiggestInterval(
                data.ports_positions,
                nodeHeight,
                'top',
                Position.Left,
            );

            topPosition = middlePosition ? middlePosition : getRoundedMiddleOfInterval(nodeHeight);
            leftPosition = 0;
        } else if (position === Position.Right) {
            const middlePosition = calculateMiddleOfBiggestInterval(
                data.ports_positions,
                nodeHeight,
                'top',
                Position.Right,
            );

            topPosition = middlePosition ? middlePosition : getRoundedMiddleOfInterval(nodeHeight);
            leftPosition = nodeWidth;
        } else if (position === Position.Top) {
            const middlePosition = calculateMiddleOfBiggestInterval(
                data.ports_positions,
                nodeWidth,
                'left',
                Position.Top,
            );

            topPosition = 0;
            leftPosition = middlePosition ? middlePosition : getRoundedMiddleOfInterval(nodeWidth);
        } else if (position === Position.Bottom) {
            const middlePosition = calculateMiddleOfBiggestInterval(
                data.ports_positions,
                nodeWidth,
                'left',
                Position.Bottom,
            );

            topPosition = nodeHeight;
            leftPosition = middlePosition ? middlePosition : getRoundedMiddleOfInterval(nodeWidth);
        }

        data.ports_positions[handleId] = {
            top: topPosition,
            left: leftPosition,
        };
    }

    return data.ports_positions[handleId];
}
